import {
  getFieldNameByParamId,
  initProductImages,
} from 'feature/appeals/helpers'
import { prepareProductsToSubmit } from 'feature/appeals/helpers/initDates/prepareProductsToSubmit'
import { prepareEditableProductObject } from 'feature/appeals/helpers/prepareEditableProductObject'
import { stateCurrentAppeal } from 'feature/appeals/redux/appealFormCreateSelector'
import { useEditAppealMutation } from 'feature/appeals/redux/appeals.api'
import { TProduct, TProductParams } from 'feature/appeals/type'
import { orderFormSpace } from 'feature/productOrder/namespaces/orderFormSpace'
import { ScreenEnum, useScreen } from 'feature/screenMode'
import { useSetNotice } from 'hooks/useSetNotice'
import { useEffect, useState } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useActions, useAppSelector } from 'store/hooks'
import { Button } from 'ui'
import { LoaderInCentalPosition } from 'widget'
import { Severity } from 'widget/Notifications'

import { InputImages } from '../ProductSelectParams/components/InputImages'
import { ProductsPropsInputType } from '../ProductSelectParams/ProductsPropsInputType'

import style from './EditProductParamsForm.module.scss'

type TEditProductParamsForm = {
  parameters: TProductParams[]
  blankedParameters: TProductParams[] | []
  product: TProduct
  onClose: () => void
}

// форма редактирования параметров заявки
export function EditProductParamsForm({
  product,
  parameters,
  blankedParameters,
  onClose,
}: TEditProductParamsForm) {
  const appeal = useAppSelector(state => state.newAppeal)
  const screen = useScreen()
  const methods = useForm()
  const { files, id } = product

  const setNotice = useSetNotice()
  const { handleSubmit } = methods
  const [editAppeal] = useEditAppealMutation()
  const [isLoading, setLoading] = useState<boolean>(false)
  const [isImagesLoading, setIsImagesLoading] = useState<boolean>(false)
  const { addAllImagesList } = useActions()
  const { allImagesList } = useAppSelector(stateCurrentAppeal)

  const [productImages, setProductImages] = useState<
    orderFormSpace.Images[][] | []
  >([])

  useEffect(() => {
    if (parameters) {
      const imagesArray = initProductImages(id, parameters, files)
      setProductImages(imagesArray)
    }
  }, [product])

  useEffect(() => {
    const hasNullUid = allImagesList.some(item => item.serverUid === null)

    setIsImagesLoading(hasNullUid)
  }, [allImagesList])

  useEffect(() => {
    return () => {
      addAllImagesList([])
    }
  }, [])

  const onSubmit = async (data: FieldValues) => {
    if (!appeal.products?.length) return
    setLoading(true)

    const editableProduct = prepareEditableProductObject({
      data: data,
      allImagesList: allImagesList.filter(
        item => item?.productId === product.id,
      ),
      productId: product.id,
    })

    const preparedDataForRequest = prepareProductsToSubmit(
      editableProduct,
      appeal.products,
      product,
      appeal,
    )

    editAppeal(preparedDataForRequest)
      .then(res => {
        setLoading(false)

        if ('error' in res) {
          //@ts-ignore
          const error = res.error.data
          setNotice(error.message, Severity.error)

          return
        }
        onClose()
        setNotice(res.data.message, Severity.success)
      })
      .catch(error => {
        setLoading(false)
        if (error.data.message) {
          setNotice(error.data.message, Severity.error)
          return
        }
        setNotice('Ошибка отправки адреса', Severity.error)
      })
  }

  const blankedImages = blankedParameters.filter(
    item => item.typeCode === 'IMAGES',
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={style.fieldsList}>
          {parameters.map(parameter => (
            <ProductsPropsInputType
              parameter={parameter}
              key={
                parameter.typeCode === 'IMAGES'
                  ? parameter.parameterValue.displayValue
                  : parameter.id
              }
            />
          ))}

          {blankedParameters.map(parameter => (
            <ProductsPropsInputType parameter={parameter} key={parameter.id} />
          ))}

          {blankedImages.map((parameter, index) => (
            <InputImages
              productId={product.id}
              paramId={+parameter.id}
              name={parameter.name}
              key={index}
            />
          ))}

          {productImages?.map((imagefiles, index) => {
            if (imagefiles[0]?.paramId)
              return (
                <InputImages
                  files={imagefiles}
                  productId={product.id}
                  paramId={+imagefiles[0].paramId}
                  name={getFieldNameByParamId(imagefiles[0].id, parameters)}
                  key={index}
                />
              )
            return <></>
          })}
        </div>
        <div className={style.buttonWrapper}>
          <Button
            variant='contained'
            type='submit'
            disabled={isLoading || isImagesLoading}
            data-testid='product-params-submit'
          >
            {isLoading && <LoaderInCentalPosition />}
            Готово
          </Button>
          <Button
            variant={screen === ScreenEnum.mobile ? 'text' : 'outlined'}
            onClick={onClose}
            className={style.cancelBtn}
            disabled={isLoading}
            data-testid='product-params-cancel'
          >
            Отменить
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
